import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  AlertTriangle,
  FileText,
  Building2,
  Map,
  Play,
  Camera,
  Upload,
  X
} from 'lucide-react';
import useImageDrop from '../utils/useImageDrop';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';

/**
 * Event Builder - Shortened builder for real emergencies
 * 5 Steps: Event Info → Scenario → Organizations → Mapping → Activate ICS
 */
const EventBuilder = ({ onBack, hiraEntry = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState({});
  const [existingEventId, setExistingEventId] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Event data state
  const [eventData, setEventData] = useState({
    event_image: null,
    event_name: '',
    event_type: '',
    event_description: '',
    location: '',
    latitude: '',
    longitude: '',
    zoom_level: 12,
    start_date: new Date().toISOString().split('T')[0],
    start_time: new Date().toTimeString().slice(0, 5),
    scenario_description: '',
    current_situation: '',
    immediate_threats: '',
    affected_area: '',
    estimated_impact: '',
    lead_agency: '',
    supporting_agencies: '',
    key_contacts: '',
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Load existing RealEvent for this HIRA entry, or pre-populate from HIRA
  useEffect(() => {
    const loadExistingEvent = async () => {
      if (!hiraEntry) return;

      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // Check for existing RealEvent linked to this HIRA
        const response = await axios.get(`${API_BASE_URL}/api/real-events`, { headers });
        const events = response.data || [];
        const matching = events.filter(e => e.hira_id === hiraEntry.id);
        // Use the most recent event if multiple exist
        const existing = matching.length > 0 ? matching[matching.length - 1] : null;

        if (existing) {
          setExistingEventId(existing.id);
          setEventData({
            event_image: existing.event_image || null,
            event_name: existing.event_name || '',
            event_type: existing.event_type || '',
            event_description: existing.event_description || '',
            location: existing.location || '',
            latitude: existing.latitude != null && !isNaN(parseFloat(existing.latitude)) ? parseFloat(existing.latitude).toFixed(4) : '',
            longitude: existing.longitude != null && !isNaN(parseFloat(existing.longitude)) ? parseFloat(existing.longitude).toFixed(4) : '',
            zoom_level: existing.zoom_level || 12,
            start_date: existing.start_date || new Date().toISOString().split('T')[0],
            start_time: existing.start_time || new Date().toTimeString().slice(0, 5),
            scenario_description: existing.scenario_description || '',
            current_situation: existing.current_situation || '',
            immediate_threats: existing.immediate_threats || '',
            affected_area: existing.affected_area || '',
            estimated_impact: existing.estimated_impact || '',
            lead_agency: existing.lead_agency || '',
            supporting_agencies: existing.supporting_agencies || '',
            key_contacts: existing.key_contacts || '',
          });
          if (existing.event_image) {
            setImagePreview({ event_image: existing.event_image });
          }
        } else {
          // No existing event — pre-populate from HIRA entry
          setEventData(prev => ({
            ...prev,
            event_name: `${hiraEntry.name} - Real Event`,
            event_type: hiraEntry.disaster_type || '',
            event_description: hiraEntry.description || '',
            latitude: hiraEntry.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
            longitude: hiraEntry.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
          }));
        }
      } catch (error) {
        console.error('Error loading existing event:', error);
        // Fallback: pre-populate from HIRA entry
        setEventData(prev => ({
          ...prev,
          event_name: `${hiraEntry.name} - Real Event`,
          event_type: hiraEntry.disaster_type || '',
          event_description: hiraEntry.description || '',
          latitude: hiraEntry.latitude != null && !isNaN(parseFloat(hiraEntry.latitude)) ? parseFloat(hiraEntry.latitude).toFixed(4) : '',
          longitude: hiraEntry.longitude != null && !isNaN(parseFloat(hiraEntry.longitude)) ? parseFloat(hiraEntry.longitude).toFixed(4) : '',
        }));
      } finally {
        setDataLoaded(true);
      }
    };

    loadExistingEvent();
  }, [hiraEntry]);

  const steps = [
    { number: 1, title: 'Event Info', description: 'Basic event information', icon: FileText },
    { number: 2, title: 'Scenario', description: 'Current situation assessment', icon: AlertTriangle },
    { number: 3, title: 'Organizations', description: 'Responding agencies', icon: Building2 },
    { number: 4, title: 'Mapping', description: 'Incident perimeter', icon: Map },
    { number: 5, title: 'Activate ICS', description: 'Launch ICS Dashboard', icon: Play }
  ];

  const validateLatitude = (value) => {
    if (value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Must be a valid number (e.g., 47.0000)';
    if (num < -90 || num > 90) return 'Must be between -90.0000 and 90.0000';
    const parts = value.split('.');
    if (parts.length > 1 && parts[1].length > 4) return 'Must have up to 4 decimal places';
    return '';
  };

  const validateLongitude = (value) => {
    if (value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Must be a valid number (e.g., -97.0000)';
    if (num < -180 || num > 180) return 'Must be between -180.0000 and 180.0000';
    const parts = value.split('.');
    if (parts.length > 1 && parts[1].length > 4) return 'Must have up to 4 decimal places';
    return '';
  };

  const handleImageUpload = (event, imageType) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setImagePreview(prev => ({ ...prev, [imageType]: imageData }));
        setEventData(prev => ({ ...prev, [imageType]: imageData }));
      };
      reader.readAsDataURL(file);
    }
  };

  const { dropProps: eventImageDropProps, isDragging: isEventImageDragging } = useImageDrop({
    onFile: (file) => handleImageUpload({ target: { files: [file] } }, 'event_image')
  });

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const buildPayload = () => ({
    event_image: eventData.event_image || null,
    event_name: eventData.event_name || `${hiraEntry?.name || 'Event'} - Real Event`,
    event_type: eventData.event_type || 'Real Event',
    event_description: eventData.event_description || '',
    location: eventData.location || '',
    latitude: parseFloat(eventData.latitude) || 0,
    longitude: parseFloat(eventData.longitude) || 0,
    zoom_level: eventData.zoom_level || 12.0,
    start_date: eventData.start_date || '',
    start_time: eventData.start_time || '',
    hira_id: hiraEntry?.id || null,
    scenario_description: eventData.scenario_description || '',
    current_situation: eventData.current_situation || '',
    immediate_threats: eventData.immediate_threats || '',
    affected_area: eventData.affected_area || '',
    estimated_impact: eventData.estimated_impact || '',
    lead_agency: eventData.lead_agency || '',
    supporting_agencies: eventData.supporting_agencies || '',
    key_contacts: eventData.key_contacts || '',
  });

  const saveProgress = async () => {
    setSaving(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const payload = buildPayload();

      if (existingEventId) {
        await axios.put(`${API_BASE_URL}/api/real-events/${existingEventId}`, payload, { headers });
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/real-events`, payload, { headers });
        if (response.data?.id) {
          setExistingEventId(response.data.id);
        }
      }
      alert('Progress saved successfully.');
    } catch (error) {
      console.error('Error saving progress:', error);
      alert('Failed to save progress. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const activateICS = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const payload = buildPayload();

      let response;
      if (existingEventId) {
        // Update existing event and navigate to its ICS
        response = await axios.put(`${API_BASE_URL}/api/real-events/${existingEventId}`, payload, { headers });
      } else {
        response = await axios.post(`${API_BASE_URL}/api/real-events`, payload, { headers });
      }

      if (response.data && (response.data.exercise_id || response.data.id)) {
        const exerciseId = response.data.exercise_id || response.data.id;
        window.location.hash = `#ics?exercise=${exerciseId}`;
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to activate ICS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderEventInfo();
      case 2:
        return renderScenario();
      case 3:
        return renderOrganizations();
      case 4:
        return renderMapping();
      case 5:
        return renderActivateICS();
      default:
        return null;
    }
  };

  const renderEventInfo = () => (
    <div className="space-y-6">
      {/* Event Image */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Event Image</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div
              {...eventImageDropProps}
              data-testid="event-image-dropzone"
              onClick={() => document.getElementById('event-image-file-input')?.click()}
              title="Click to browse or drop an image here"
              className={`w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed cursor-pointer transition-colors ${
                isEventImageDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
              }`}
            >
              {imagePreview.event_image ? (
                <img src={imagePreview.event_image} alt="Event" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-gray-500 px-2">
                  <Camera className="h-8 w-8 mx-auto mb-2" />
                  <span className="text-xs">{isEventImageDragging ? 'Drop to upload' : 'No image · Drop here'}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="cursor-pointer">
                <input
                  id="event-image-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'event_image')}
                />
                <Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10" asChild>
                  <span><Upload className="h-4 w-4 mr-2" />Upload Photo</span>
                </Button>
              </label>
              <p className="text-xs text-gray-500">Upload image of the event/incident — or drag &amp; drop</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Details */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Event Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Event Name *</label>
            <Input
              value={eventData.event_name}
              onChange={(e) => setEventData(prev => ({ ...prev, event_name: e.target.value }))}
              placeholder="Enter event name"
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="event-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Event Type *</label>
            <Select
              value={eventData.event_type}
              onValueChange={(value) => setEventData(prev => ({ ...prev, event_type: value }))}
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                <SelectItem value="Fire" className="text-white">Fire</SelectItem>
                <SelectItem value="Flood" className="text-white">Flood</SelectItem>
                <SelectItem value="Earthquake" className="text-white">Earthquake</SelectItem>
                <SelectItem value="Hurricane" className="text-white">Hurricane</SelectItem>
                <SelectItem value="Tornado" className="text-white">Tornado</SelectItem>
                <SelectItem value="Hazmat" className="text-white">Hazmat Incident</SelectItem>
                <SelectItem value="Mass Casualty" className="text-white">Mass Casualty</SelectItem>
                <SelectItem value="Active Threat" className="text-white">Active Threat</SelectItem>
                <SelectItem value="Infrastructure Failure" className="text-white">Infrastructure Failure</SelectItem>
                <SelectItem value="Other" className="text-white">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <Textarea
              value={eventData.event_description}
              onChange={(e) => setEventData(prev => ({ ...prev, event_description: e.target.value }))}
              placeholder="Describe the event"
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
            <Input
              value={eventData.location}
              onChange={(e) => setEventData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Enter location address or description"
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="event-location-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Latitude (Decimal: -90 to 90)</label>
              <Input
                value={eventData.latitude}
                onChange={(e) => {
                  setEventData(prev => ({ ...prev, latitude: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, latitude: validateLatitude(e.target.value) }));
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val !== '') {
                    const num = parseFloat(val);
                    if (isNaN(num)) {
                      alert('Latitude must be a valid number (e.g., 47.0000)');
                    } else if (num < -90 || num > 90) {
                      alert('Latitude must be between -90.0000 and 90.0000');
                    } else {
                      const parts = val.split('.');
                      if (parts.length > 1 && parts[1].length > 4) {
                        alert('Latitude must have up to 4 decimal places (e.g., 47.0000)');
                      } else {
                        setEventData(prev => ({ ...prev, latitude: num.toFixed(4) }));
                      }
                    }
                  }
                }}
                placeholder="47.0000"
                className={`bg-gray-700 border-gray-600 text-white ${validationErrors.latitude ? 'border-red-500' : ''}`}
              />
              {validationErrors.latitude && <p className="text-red-400 text-xs mt-1">{validationErrors.latitude}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Longitude (Decimal: -180 to 180)</label>
              <Input
                value={eventData.longitude}
                onChange={(e) => {
                  setEventData(prev => ({ ...prev, longitude: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, longitude: validateLongitude(e.target.value) }));
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val !== '') {
                    const num = parseFloat(val);
                    if (isNaN(num)) {
                      alert('Longitude must be a valid number (e.g., -97.0000)');
                    } else if (num < -180 || num > 180) {
                      alert('Longitude must be between -180.0000 and 180.0000');
                    } else {
                      const parts = val.split('.');
                      if (parts.length > 1 && parts[1].length > 4) {
                        alert('Longitude must have up to 4 decimal places (e.g., -97.0000)');
                      } else {
                        setEventData(prev => ({ ...prev, longitude: num.toFixed(4) }));
                      }
                    }
                  }
                }}
                placeholder="-97.0000"
                className={`bg-gray-700 border-gray-600 text-white ${validationErrors.longitude ? 'border-red-500' : ''}`}
              />
              {validationErrors.longitude && <p className="text-red-400 text-xs mt-1">{validationErrors.longitude}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Zoom Level</label>
              <Input
                type="number"
                min="1"
                max="20"
                value={eventData.zoom_level}
                onChange={(e) => setEventData(prev => ({ ...prev, zoom_level: parseInt(e.target.value) || 12 }))}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Date *</label>
              <Input
                type="date"
                value={eventData.start_date}
                onChange={(e) => setEventData(prev => ({ ...prev, start_date: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Time *</label>
              <Input
                type="time"
                value={eventData.start_time}
                onChange={(e) => setEventData(prev => ({ ...prev, start_time: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderScenario = () => (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Current Situation Assessment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Scenario Description *</label>
            <Textarea
              value={eventData.scenario_description}
              onChange={(e) => setEventData(prev => ({ ...prev, scenario_description: e.target.value }))}
              placeholder="Describe the current scenario and situation"
              className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
              data-testid="event-scenario-description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Current Situation</label>
            <Textarea
              value={eventData.current_situation}
              onChange={(e) => setEventData(prev => ({ ...prev, current_situation: e.target.value }))}
              placeholder="What is happening right now?"
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
              data-testid="event-current-situation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Immediate Threats</label>
            <Textarea
              value={eventData.immediate_threats}
              onChange={(e) => setEventData(prev => ({ ...prev, immediate_threats: e.target.value }))}
              placeholder="Identify immediate threats to life, property, and environment"
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
              data-testid="event-immediate-threats"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Affected Area</label>
              <Input
                value={eventData.affected_area}
                onChange={(e) => setEventData(prev => ({ ...prev, affected_area: e.target.value }))}
                placeholder="e.g., 5 square miles"
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="event-affected-area"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Estimated Impact</label>
              <Select
                value={eventData.estimated_impact}
                onValueChange={(value) => setEventData(prev => ({ ...prev, estimated_impact: value }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="event-estimated-impact">
                  <SelectValue placeholder="Select impact level" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="Minor" className="text-white">Minor - Limited impact</SelectItem>
                  <SelectItem value="Moderate" className="text-white">Moderate - Significant impact</SelectItem>
                  <SelectItem value="Major" className="text-white">Major - Widespread impact</SelectItem>
                  <SelectItem value="Catastrophic" className="text-white">Catastrophic - Severe impact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderOrganizations = () => (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Responding Organizations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Lead Agency *</label>
            <Input
              value={eventData.lead_agency}
              onChange={(e) => setEventData(prev => ({ ...prev, lead_agency: e.target.value }))}
              placeholder="Enter the lead responding agency"
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="event-lead-agency"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Supporting Agencies</label>
            <Textarea
              value={eventData.supporting_agencies}
              onChange={(e) => setEventData(prev => ({ ...prev, supporting_agencies: e.target.value }))}
              placeholder="List supporting agencies (one per line)"
              className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
              data-testid="event-supporting-agencies"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Key Contacts</label>
            <Textarea
              value={eventData.key_contacts}
              onChange={(e) => setEventData(prev => ({ ...prev, key_contacts: e.target.value }))}
              placeholder="List key contacts with phone numbers"
              className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
              data-testid="event-key-contacts"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderMapping = () => (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Incident Mapping</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-700/50 rounded-lg p-8 text-center border-2 border-dashed border-gray-600">
            <Map className="h-16 w-16 mx-auto text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">Mapping Available in ICS</h3>
            <p className="text-gray-400 text-sm mb-4">
              Once you activate ICS, you'll have full access to the mapping tools to:
            </p>
            <ul className="text-left text-gray-400 text-sm space-y-2 max-w-md mx-auto">
              <li className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Draw incident perimeter boundaries
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Place markers for command posts, staging areas
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Mark evacuation routes and zones
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Track resource locations in real-time
              </li>
            </ul>
            <p className="text-orange-400 text-sm mt-4">
              Location: {eventData.location || 'Not set'} ({eventData.latitude || '0'}, {eventData.longitude || '0'})
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderActivateICS = () => (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-orange-500">Ready to Activate ICS</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-red-400 mb-2">Real Event Activation</h3>
                <p className="text-gray-300 text-sm">
                  You are about to activate the Incident Command System for a <strong>REAL EVENT</strong>.
                  This will create an active incident record and open the ICS Dashboard in Response mode.
                </p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">Event Summary</h4>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Event Name:</span>
                <p className="text-white font-medium">{eventData.event_name || 'Not set'}</p>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Event Type:</span>
                <p className="text-white font-medium">{eventData.event_type || 'Not set'}</p>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Location:</span>
                <p className="text-white font-medium">{eventData.location || 'Not set'}</p>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Start:</span>
                <p className="text-white font-medium">{eventData.start_date} {eventData.start_time}</p>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Lead Agency:</span>
                <p className="text-white font-medium">{eventData.lead_agency || 'Not set'}</p>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <span className="text-gray-400">Impact Level:</span>
                <p className="text-white font-medium">{eventData.estimated_impact || 'Not set'}</p>
              </div>
            </div>

            {hiraEntry && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
                <p className="text-orange-400 text-sm">
                  <strong>Linked to HIRA:</strong> {hiraEntry.name} ({hiraEntry.disaster_type})
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Button
              onClick={activateICS}
              disabled={loading || !eventData.event_name}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
              data-testid="event-activate-ics-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Activating...
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Activate ICS Dashboard
                </>
              )}
            </Button>
            <p className="text-gray-500 text-xs mt-2">
              This will create the incident and open ICS in Response mode
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to HIRA
            </Button>
            <div className="h-6 w-px bg-gray-700"></div>
            <div>
              <h1 className="text-2xl font-bold text-red-500">Event Builder</h1>
              <p className="text-sm text-gray-400">Real Emergency Response Activation</p>
            </div>
          </div>
          {hiraEntry && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-2">
              <span className="text-orange-400 text-sm">
                Based on HIRA: <strong>{hiraEntry.name}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex">
        {/* Step Sidebar */}
        <div className="w-72 bg-gray-800 border-r border-gray-700 min-h-[calc(100vh-80px)] p-4">
          <h2 className="text-red-500 font-bold text-lg mb-6">Event Builder</h2>
          <div className="space-y-2">
            {steps.map((step) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <button
                  key={step.number}
                  onClick={() => setCurrentStep(step.number)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-red-500 text-white'
                      : isCompleted
                      ? 'bg-gray-700 text-green-400 hover:bg-gray-600'
                      : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isActive
                        ? 'bg-white text-red-500'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-600 text-gray-300'
                    }`}>
                      {isCompleted ? <Check className="h-4 w-4" /> : step.number}
                    </div>
                    <div>
                      <div className="font-medium">{step.title}</div>
                      <div className={`text-xs ${isActive ? 'text-red-100' : 'text-gray-500'}`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            {/* Step Header */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-red-500">
                Step {currentStep}: {steps[currentStep - 1].title}
              </h2>
              <p className="text-gray-400">{steps[currentStep - 1].description}</p>
            </div>

            {/* Step Content */}
            {renderStepContent()}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                data-testid="event-builder-prev-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              <div className="flex space-x-3">
                {currentStep < steps.length && (
                  <Button
                    variant="outline"
                    onClick={saveProgress}
                    disabled={saving}
                    className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                    data-testid="event-builder-save-btn"
                  >
                    {saving ? 'Saving...' : 'Save Progress'}
                  </Button>
                )}
                {currentStep < steps.length ? (
                  <Button
                    onClick={nextStep}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="event-builder-next-btn"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventBuilder;
